import React, { useEffect, useState } from 'react';
import { Feedback, useSectionFeedback } from './sectionFeedback';

function previewUrl(currentUrl, slug) {
    if (!currentUrl || !slug) return currentUrl || '';
    try {
        const url = new URL(currentUrl);
        const hostParts = url.hostname.split('.').filter(Boolean);
        url.hostname = hostParts.length > 1
            ? [slug, ...hostParts.slice(1)].join('.')
            : `${slug}.${url.hostname}`;
        url.pathname = '/';
        url.search = '';
        url.hash = '';
        return url.toString();
    } catch {
        return currentUrl.replace(/^(https?:\/\/)?([^/:]+)(:\d+)?(.*)$/i, (match, protocol = '', hostname, port = '') => {
            const hostParts = hostname.split('.').filter(Boolean);
            const nextHostname = hostParts.length > 1
                ? [slug, ...hostParts.slice(1)].join('.')
                : `${slug}.${hostname}`;
            return `${protocol}${nextHostname}${port || ''}/`;
        });
    }
}

export default function AddressSection({ data, saving, onSave, actions }) {
    const [slug, setSlug] = useState(data?.slug || '');
    const [availability, setAvailability] = useState({ status: 'idle', message: '' });
    const { feedback, showFeedback } = useSectionFeedback();
    const storefrontUrl = previewUrl(data?.url, slug);

    useEffect(() => {
        setSlug(data?.slug || '');
    }, [data?.slug]);

    useEffect(() => {
        const cleanSlug = String(slug || '').toLowerCase().replace(/[^a-z0-9-]/g, '');
        if (!cleanSlug) {
            setAvailability({ status: 'idle', message: '' });
            return undefined;
        }

        if (cleanSlug === String(data?.slug || '').toLowerCase()) {
            setAvailability({ status: 'available', message: 'This is your current storefront address.' });
            return undefined;
        }

        let cancelled = false;
        setAvailability({ status: 'checking', message: 'Checking availability...' });
        const timeout = setTimeout(async () => {
            const result = await actions.checkSlugAvailability(cleanSlug);
            if (cancelled) return;
            if (!result.success) {
                setAvailability({ status: 'error', message: result.error || 'Could not check availability.' });
            } else if (result.available) {
                setAvailability({ status: 'available', message: 'This storefront address is available.' });
            } else {
                setAvailability({ status: 'taken', message: result.error || 'This storefront address is not available.' });
            }
        }, 450);

        return () => {
            cancelled = true;
            clearTimeout(timeout);
        };
    }, [slug, data?.slug, actions.checkSlugAvailability]);

    function handleSave() {
        const cleanSlug = String(slug || '').toLowerCase().replace(/[^a-z0-9-]/g, '');
        if (!cleanSlug) {
            showFeedback('error', 'Enter a URL slug.');
            return;
        }
        if (cleanSlug !== slug) setSlug(cleanSlug);

        onSave(async () => {
            const result = await actions.saveSlug(cleanSlug);
            showFeedback(result.success ? 'success' : 'error', result.success ? 'URL saved.' : result.error);
            return result;
        });
    }

    return (
        <div>
            <div className="sfm-section-header">
                <h2>Website URL</h2>
                <p>Your public storefront URL. Choose a short, memorable slug that identifies your store.</p>
            </div>
            <div className="group">
                <label htmlFor="sfm-slug">Slug</label>
                <input
                    id="sfm-slug"
                    type="text"
                    value={slug}
                    onChange={event => setSlug(event.target.value)}
                    placeholder="my-storefront-name"
                    aria-describedby="sfm-slug-help sfm-slug-status"
                    aria-invalid={availability.status === 'taken' || availability.status === 'error' ? true : undefined}
                    autoComplete="off"
                    spellCheck={false}
                />
                <p id="sfm-slug-help" className="help">Lowercase letters, numbers, and hyphens only. Example: my-storefront.</p>
                <p
                    id="sfm-slug-status"
                    aria-live="polite"
                    className={`help sfm-slug-status sfm-slug-status-${availability.status}`}
                >
                    {availability.message}
                </p>
            </div>
            <div className="group">
                <label htmlFor="sfm-url">Your Storefront Address</label>
                <input
                    id="sfm-url"
                    type="text"
                    value={storefrontUrl}
                    readOnly
                    aria-readonly="true"
                    aria-describedby="sfm-url-help"
                />
                <p id="sfm-url-help" className="help">
                    {storefrontUrl
                        ? <a href={storefrontUrl} target="_blank" rel="noreferrer">Open your storefront</a>
                        : 'Enter a slug above to see your storefront URL.'}
                </p>
            </div>
            <div className="sfm-save-row">
                <button
                    type="button"
                    className="button primary"
                    onClick={handleSave}
                    disabled={saving || !String(slug || '').toLowerCase().replace(/[^a-z0-9-]/g, '') || availability.status === 'checking' || availability.status === 'taken' || availability.status === 'error'}
                >
                    Save URL Details
                </button>
                <Feedback feedback={feedback} />
            </div>
        </div>
    );
}
